import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CreateAssignmentModal from '../../components/CreateAssignmentModal';
import OutcomePicker, { OutcomeOptions } from '../../components/OutcomePicker';
import { teacherApi } from '../../services/api';

interface Assignment {
  id: number;
  title: string;
  subject: string;
  openDate: string;
  closeDate: string;
  closeDateRaw: string | null;
  institution: string;
  classes: string;
  color: string;
  archived: boolean;
  contentType: string;
  book: string;
  unit: string;
  note: string; // B28: free-text açıklama (Diğer Ödev), markers stripped
  tests: string[];
  classDetails: { id: string | null; name: string; students: number; completed: number }[];
  stats: { total: number; submittedCount: number; gradedCount: number; pendingCount: number; avgScore: number | null; isExpired: boolean };
  bookId?: number | null;
  sectionLabel?: string | null;
  attachmentUrl?: string | null;
  answerKey?: string | null;
  answersReleased?: boolean;
  visibleToStudents: boolean; // B22: false = hidden from students (teacher still sees it)
  learningOutcomes: { type: 'meb' | 'book'; id: number; code: string | null; label: string }[];
}

// T25: son tarih — tarih + saat her zaman gösterilir (23:59 dahil), ör.
// "25.07.2026 23:59". due_date bir UTC anıdır; TR duvar-saati sabit UTC+3.
// Yalnızca gösterim.
function fmtDeadline(iso: string | null | undefined): string {
  if (!iso) return 'Süresiz';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Süresiz';
  const tr = new Date(d.getTime() + 3 * 3600 * 1000);
  const dd = String(tr.getUTCDate()).padStart(2, '0');
  const mo = String(tr.getUTCMonth() + 1).padStart(2, '0');
  const yy = tr.getUTCFullYear();
  const hh = tr.getUTCHours();
  const mn = tr.getUTCMinutes();
  return `${dd}.${mo}.${yy} ${String(hh).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
}

function mapAssignment(a: any): Assignment {
  const cd = (a.assignment_class_targets ?? []).map((t: any) => ({
    id: t.classes?.id != null ? String(t.classes.id) : null,
    name: t.classes?.name ?? '—',
    students: t.classes?._count?.class_student_enrollments ?? t.classes?.class_student_enrollments?.length ?? 0,
    completed: t.completed_count ?? 0,
  }));
  // Individual student targets (bireysel ödevler)
  const studentTargets = (a.assignment_student_targets ?? []).map((t: any) => ({
    id: null,
    name: t.users ? `${t.users.first_name ?? ''} ${t.users.last_name ?? ''}`.trim() : 'Öğrenci',
    students: 1,
    completed: 0,
  }));
  const classDetails = cd.length > 0 ? cd : studentTargets;
  const stats = a._stats ?? { total: 0, submittedCount: 0, gradedCount: 0, pendingCount: 0, avgScore: null, isExpired: false };
  return {
    id: Number(a.id),
    title: a.title ?? '—',
    subject: a.subject?.name ?? '',
    openDate: a.createdAt ? new Date(a.createdAt).toLocaleDateString('tr-TR') : '—',
    closeDate: fmtDeadline(a.due_date),
    closeDateRaw: a.due_date ?? null,
    institution: cd[0] ? (a.assignment_class_targets?.[0]?.classes?.campuses?.institutions?.name ?? '') : '',
    classes: cd.length > 0
      ? cd.map((c: any) => c.name).join(', ')
      : studentTargets.map((s: any) => s.name).join(', ') || '—',
    color: stats.isExpired ? '#c5c5c5' : '#85f57b',
    archived: stats.isExpired,
    contentType: ((): string => {
      const t = a.assignment_type;
      // Eski türler (book_assignment, weekly_scan, video) artık tek "Test Ödevi"
      // başlığı altında. "Diğer" ayrı kalıyor.
      if (t === 'other') return 'Diğer Ödev';
      return 'Test Ödevi';
    })(),
    book: a.book_link?.book_title ?? (a.book_link?.book_id ? `Kitap #${a.book_link.book_id}` : (a.content_items?.books?.title ?? '—')),
    unit: a.content_items?.subject_units?.name ?? '—',
    note: a.note ?? '',
    tests: a.book_link?.section_label ? [a.book_link.section_label] : [],
    classDetails,
    stats,
    // Extra fields used for navigation
    bookId: a.book_link?.book_id ?? null,
    sectionLabel: a.book_link?.section_label ?? null,
    attachmentUrl: a.attachment_url ?? null,
    answerKey: a.answer_key ?? null,
    answersReleased: !!a.answers_released,
    visibleToStudents: a.visible_to_students !== false,
    learningOutcomes: a.learning_outcomes ?? [],
  } as any;
}

interface SubmissionRow {
  id: string;
  name: string;
  score: number | null;
  status: string;
  time: string;
  submissionFileUrl: string | null;
  studentNotes: string | null;
  feedback: string | null;
  classId: string | null;   // B32: which target class this student belongs to
  className: string | null;
  late: boolean;            // turned in after the deadline ("Geç Teslim")
}

function mapSubmission(s: any): SubmissionRow {
  const first = s.users?.first_name ?? '';
  const last = s.users?.last_name ?? '';
  const grade = s.assignment_grades;
  const secs = s.timeSpentSeconds ?? s.time_spent_seconds ?? null;
  const totalSecs = typeof secs === 'number' ? secs : null;
  const timeStr = totalSecs != null
    ? totalSecs >= 60
      ? `${Math.floor(totalSecs / 60)} Dk ${totalSecs % 60 > 0 ? `${totalSecs % 60} Sn` : ''}`
      : `${totalSecs} Sn`
    : '—';
  return {
    id: String(s.id),
    name: `${first} ${last}`.trim() || 'Öğrenci',
    score: grade?.score != null ? Number(grade.score) : null,
    status: s.status ?? 'pending',
    time: timeStr,
    submissionFileUrl: s.submission_file_url ?? null,
    studentNotes: s.student_notes ?? null,
    feedback: grade?.feedback ?? null,
    classId: s.class_id != null ? String(s.class_id) : null,
    className: s.class_name ?? null,
    late: !!s.late,
  };
}

// ---- Assignment Card ----
const AssignmentCard: React.FC<{ hw: Assignment; onClick: () => void }> = ({ hw, onClick }) => {
  const pct = hw.stats.total > 0 ? Math.round((hw.stats.submittedCount / hw.stats.total) * 100) : 0;
  return (
    <div
      onClick={onClick}
      className="bg-[#fafafa] rounded-[12px] p-4 cursor-pointer hover:shadow-md transition-shadow relative"
      style={{ width: 353, boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
    >
      <div className="flex gap-3 mb-2">
        <div className="w-[65px] h-[65px] rounded-[8px] flex items-center justify-center flex-shrink-0" style={{ background: hw.color }}>
          <img src="/a-odevicon.png" alt="" className="w-[33px] h-[31px] object-contain" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#2b2b2b' }}>{hw.title}</p>
            {!hw.visibleToStudents && (
              <span style={{ fontFamily: 'Inter', fontSize: 10, fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 9999, padding: '2px 8px' }}>
                Öğrenciye kapalı
              </span>
            )}
          </div>
          <p style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: '#888' }}>Kapanış: {hw.closeDate}</p>
          <p style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 600, color: '#3973bf' }}>
            {hw.contentType} · {hw.classes !== '—' ? hw.classes : 'Bireysel Ödev'}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-2">
        <div className="flex justify-between text-[12px] mb-1" style={{ fontFamily: 'Inter' }}>
          <span style={{ color: '#2b2b2b', fontWeight: 600 }}>Tamamlama</span>
          <span style={{ color: '#3973bf', fontWeight: 700 }}>{hw.stats.submittedCount}/{hw.stats.total}</span>
        </div>
        <div className="w-full h-[6px] rounded-full bg-gray-200 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: pct + '%', background: hw.stats.isExpired ? '#c5c5c5' : '#e5599b' }} />
        </div>
        <div className="flex gap-3 mt-2 text-[11px]" style={{ fontFamily: 'Inter' }}>
          <span style={{ color: '#12D700', fontWeight: 600 }}>● {hw.stats.gradedCount} notlandı</span>
          <span style={{ color: '#f1972f', fontWeight: 600 }}>● {hw.stats.submittedCount - hw.stats.gradedCount} bekliyor</span>
          <span style={{ color: '#c5c5c5', fontWeight: 600 }}>● {hw.stats.pendingCount} yapılmadı</span>
        </div>
        {hw.stats.avgScore != null && (
          <p className="mt-1 text-[12px]" style={{ fontFamily: 'Inter', color: '#2b2b2b' }}>
            Ortalama: <span style={{ fontWeight: 700, color: '#e5599b' }}>{hw.stats.avgScore}</span>
          </p>
        )}
      </div>

      {/* Expired badge */}
      {hw.stats.isExpired ? (
        <div className="absolute top-3 right-3 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: '#c5c5c5', color: '#fff', fontFamily: 'Inter' }}>
          Süresi Doldu
        </div>
      ) : (
        <div className="absolute top-3 right-3 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: '#85f57b', color: '#1e1e1e', fontFamily: 'Inter' }}>
          Aktif
        </div>
      )}
    </div>
  );
};


// ---- D6: MEB kazanımı / kitap konusu chips + edit modal ----
const AssignmentOutcomes: React.FC<{
  hw: Assignment;
  onSaved: (los: Assignment['learningOutcomes']) => void;
}> = ({ hw, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<OutcomeOptions | null>(null);
  const [mebIds, setMebIds] = useState<number[]>([]);
  const [bookUnitIds, setBookUnitIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const openEditor = async () => {
    setOpen(true); setOpts(null); setErr(null);
    try {
      const { data } = await teacherApi.assignmentOutcomeOptions({ assignmentId: hw.id });
      setOpts(data as OutcomeOptions);
      const cur = (data.current ?? []) as Assignment['learningOutcomes'];
      setMebIds(cur.filter(c => c.type === 'meb').map(c => c.id));
      setBookUnitIds(cur.filter(c => c.type === 'book').map(c => c.id));
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? 'MEB kazanımları ve kitap konuları yüklenemedi');
    }
  };

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const { data } = await teacherApi.setAssignmentOutcomes(hw.id, { mebIds, bookUnitIds });
      onSaved(data.learning_outcomes ?? []);
      setOpen(false);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-2">
        <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#3973bf' }}>MEB Kazanımları / Kitap Konuları:</p>
        <button onClick={openEditor} className="rounded-full px-3 py-1 hover:opacity-90"
          style={{ background: '#fdeef5', color: '#e5599b', fontFamily: 'Inter', fontSize: 12, fontWeight: 600 }}>
          Düzenle
        </button>
      </div>
      {hw.learningOutcomes.length === 0 ? (
        <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#888' }}>MEB kazanımı veya kitap konusu eklenmemiş.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {hw.learningOutcomes.map((o, i) => (
            <span key={i} className="rounded-full px-3 py-1"
              style={{
                background: o.type === 'meb' ? '#eef3fd' : '#fdeef5',
                color: o.type === 'meb' ? '#3973bf' : '#e5599b',
                fontFamily: 'Inter', fontSize: 12, fontWeight: 500,
              }}>
              {o.type === 'meb' ? 'MEB · ' : 'Konu · '}{o.code ? `${o.code} · ` : ''}{o.label.length > 60 ? `${o.label.slice(0, 60)}…` : o.label}
            </span>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setOpen(false)} />
          <div className="relative bg-white rounded-[16px] w-full max-w-[640px] p-6" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
            <p style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 700, color: '#2b2b2b', marginBottom: 12 }}>MEB Kazanımları / Kitap Konuları</p>
            {opts ? (
              <OutcomePicker options={opts} mebIds={mebIds} bookUnitIds={bookUnitIds}
                onChange={sel => { setMebIds(sel.mebIds); setBookUnitIds(sel.bookUnitIds); }} />
            ) : !err ? (
              <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#888' }}>Yükleniyor…</p>
            ) : null}
            {err && <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#e11d48', marginTop: 8 }}>{err}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setOpen(false)} className="rounded-[10px] px-4 py-2"
                style={{ background: '#f3f4f6', color: '#555', fontFamily: 'Inter', fontSize: 14 }}>İptal</button>
              <button onClick={save} disabled={saving || !opts} className="rounded-[10px] px-4 py-2 text-white disabled:opacity-50"
                style={{ background: '#e5599b', fontFamily: 'Inter', fontSize: 14 }}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type View = 'list' | 'detail' | 'performance';
type Tab = 'active' | 'expired';
type TargetFilter = 'all' | 'class' | 'individual';

const TeacherAssignments: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [view, setView] = useState<View>(id ? 'detail' : 'list');
  const [tab, setTab] = useState<Tab>('active');
  const [targetFilter, setTargetFilter] = useState<TargetFilter>('all');
  const [selectedId, setSelectedId] = useState<number>(id ? Number(id) : 0);
  // B32: 'all' = "Bu ödevin atandığı bütün öğrenciler"; otherwise a class id.
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [studentQuery, setStudentQuery] = useState('');
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState<SubmissionRow[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  // Per-student submission grading popup.
  const [gradingFor, setGradingFor] = useState<SubmissionRow | null>(null);
  const [gradeScore, setGradeScore] = useState('');
  const [gradeComment, setGradeComment] = useState('');
  const [savingGrade, setSavingGrade] = useState(false);
  const [gradeError, setGradeError] = useState<string | null>(null);

  const loadAssignments = () => {
    return teacherApi.assignments()
      .then((res) => {
        const mapped = (res.data || []).map(mapAssignment);
        setAssignments(mapped);
        if (mapped.length > 0 && !selectedId) setSelectedId(mapped[0].id);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    loadAssignments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [createOpen, setCreateOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);

  const isIndividual = (a: Assignment) => (a as any).bookId !== undefined
    ? a.classDetails.length > 0 && a.classDetails[0].students === 1 && !a.classes.includes(',')
    : false;

  const applyTargetFilter = (list: Assignment[]) => {
    if (targetFilter === 'class') return list.filter(a => a.classes.includes(',') || (a.classDetails[0]?.students ?? 0) > 1);
    if (targetFilter === 'individual') return list.filter(a => (a.classDetails[0]?.students ?? 0) <= 1 || a.institution === '');
    return list;
  };

  const activeHw = applyTargetFilter(assignments.filter(a => !a.stats.isExpired));
  const expiredHw = applyTargetFilter(assignments.filter(a => a.stats.isExpired));
  const visibleHw = tab === 'active' ? activeHw : expiredHw;
  const selected = assignments.find(a => a.id === selectedId) || assignments[0];

  // Figma "Ödevi Bitir" popup (file 6nrQCBxlgi5ODYgeroTZk8 node 855-4764).
  // Native window.confirm gone — replaced with the styled modal below.
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  // "Ödev İçeriğini Görüntüle" — inline PDF viewer for Diğer Ödev attachments.
  const [pdfOpen, setPdfOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  // "Cevapları Yayınla" — mark the homework complete for the class so students
  // get a "Cevapları Görüntüle" button.
  const releaseAnswers = async () => {
    if (!selected) return;
    setReleasing(true);
    try {
      await teacherApi.releaseAnswers(selected.id);
      const res = await teacherApi.assignments();
      setAssignments((res.data || []).map(mapAssignment));
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Cevaplar yayınlanamadı');
    } finally {
      setReleasing(false);
    }
  };
  const closeSelected = () => {
    if (!selected) return;
    setCloseConfirmOpen(true);
  };
  // B22: "Ödevi Bitir" is now a reversible student-visibility toggle. Closing
  // hides the homework from students but keeps it in the teacher's list (with a
  // badge) so they can re-open it; nothing is deleted.
  const confirmCloseAssignment = async () => {
    if (!selected) return;
    const next = !selected.visibleToStudents; // target visibility
    setClosing(true);
    try {
      await teacherApi.setAssignmentVisibility(selected.id, next);
      setAssignments(prev => prev.map(a => a.id === selected.id ? { ...a, visibleToStudents: next } : a));
      setCloseConfirmOpen(false);
    } catch (err: any) {
      alert(err?.response?.data?.error ?? 'Ödev görünürlüğü değiştirilemedi');
    } finally {
      setClosing(false);
    }
  };

  const openDetail = (hwId: number) => {
    setSelectedId(hwId);
    setView('detail');
  };

  // "Ödevi Düzenle" — edits the EXISTING assignment (title + son tarih) via
  // PATCH, instead of the old broken behaviour that re-ran the create wizard
  // and produced a duplicate. Book/section/targets stay fixed.
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('23:59');
  const [editIndefinite, setEditIndefinite] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const openEdit = () => {
    if (!selected) return;
    setEditTitle(selected.title === '—' ? '' : selected.title);
    const raw = selected.closeDateRaw;
    if (raw) {
      const tr = new Date(new Date(raw).getTime() + 3 * 3600 * 1000); // TR wall-clock
      const p = (n: number) => String(n).padStart(2, '0');
      setEditDate(`${tr.getUTCFullYear()}-${p(tr.getUTCMonth() + 1)}-${p(tr.getUTCDate())}`);
      setEditTime(`${p(tr.getUTCHours())}:${p(tr.getUTCMinutes())}`);
      setEditIndefinite(false);
    } else {
      setEditDate(''); setEditTime('23:59'); setEditIndefinite(true);
    }
    setEditError(null);
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!selected) return;
    if (!editTitle.trim()) { setEditError('Başlık boş olamaz.'); return; }
    setEditSaving(true); setEditError(null);
    try {
      await teacherApi.updateAssignment(selected.id, {
        title: editTitle.trim(),
        indefinite: editIndefinite,
        // Naive "YYYY-MM-DDTHH:mm" (school-clock TR) — backend resolveSchoolDeadline.
        dueDate: editIndefinite ? null : (editDate ? `${editDate}T${editTime || '23:59'}` : undefined),
      });
      await loadAssignments();
      setEditOpen(false);
    } catch (e: any) {
      setEditError(e?.response?.data?.error ?? 'Ödev güncellenemedi.');
    } finally {
      setEditSaving(false);
    }
  };

  const loadSubmissions = () => {
    if (!selectedId) return;
    setLoadingSubmissions(true);
    teacherApi.assignmentSubmissions(selectedId)
      .then((res) => setSubmissions((res.data || []).map(mapSubmission)))
      .catch(console.error)
      .finally(() => setLoadingSubmissions(false));
  };

  const openPerformance = () => {
    setSelectedClass('all');   // B32: default to the "all students" view
    setStudentQuery('');
    setView('performance');
    loadSubmissions();
  };

  // Open the per-student grading popup, prefilled with any existing grade.
  const openGrading = (s: SubmissionRow) => {
    setGradingFor(s);
    setGradeScore(s.score != null ? String(s.score) : '');
    setGradeComment(s.feedback ?? '');
    setGradeError(null);
  };

  const saveGrade = async () => {
    if (!gradingFor) return;
    const scoreNum = Number(gradeScore);
    if (gradeScore.trim() === '' || Number.isNaN(scoreNum) || scoreNum < 0 || scoreNum > 100) {
      setGradeError('Lütfen 0-100 arası bir puan girin.');
      return;
    }
    setSavingGrade(true);
    setGradeError(null);
    try {
      await teacherApi.gradeSubmission(gradingFor.id, { score: scoreNum, feedback: gradeComment.trim() || undefined });
      setGradingFor(null);
      loadSubmissions();
    } catch (e: any) {
      setGradeError(e?.response?.data?.error ?? e?.message ?? 'Kaydedilemedi.');
    } finally {
      setSavingGrade(false);
    }
  };

  const modalEl = <CreateAssignmentModal open={createOpen} onClose={() => { setCreateOpen(false); setEditData(null); }} onCreated={loadAssignments} />;

  // "Ödevi Bitir" confirm popup (Figma node 855-4764). Extracted so it can be
  // rendered in every view — the button lives in detail/performance, so if the
  // modal only rendered in the list branch it wouldn't show until you navigated
  // back to the list.
  const closeConfirmEl = closeConfirmOpen && selected ? (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={() => !closing && setCloseConfirmOpen(false)} />
      <div
        className="relative bg-[#f6f6f6] rounded-[24px] flex flex-col items-center"
        style={{ width: 460, maxWidth: '92vw', padding: '36px 32px 28px', boxShadow: '0px 8px 32px rgba(0,0,0,0.18)' }}
      >
        <button
          onClick={() => !closing && setCloseConfirmOpen(false)}
          aria-label="Kapat"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/90 hover:bg-gray-100 flex items-center justify-center"
          style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.08)' }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
            <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
          </svg>
        </button>

        <div className="w-[50px] h-[50px] rounded-full bg-[#3973bf] flex items-center justify-center mb-4" style={{ boxShadow: '0px 2px 6px rgba(57,115,191,0.3)' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </div>

        <p style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#2b2b2b', textAlign: 'center', marginBottom: 8 }}>
          {selected.visibleToStudents ? 'Ödevi Öğrenciye Kapatmak İstiyor Musunuz?' : 'Ödevi Öğrenciye Açmak İstiyor Musunuz?'}
        </p>
        <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#6b7280', textAlign: 'center', marginBottom: 24 }}>
          {selected.visibleToStudents
            ? 'Bu ödev öğrenci listelerinden gizlenecek. İstediğiniz zaman tekrar açabilirsiniz.'
            : 'Bu ödev tekrar öğrencilere görünür olacak.'}
        </p>

        <div className="flex gap-3 w-full">
          <button
            onClick={confirmCloseAssignment}
            disabled={closing}
            className="flex-1 rounded-[14px] py-3 hover:opacity-90 disabled:opacity-50 transition-opacity"
            style={{ background: '#3973bf' }}
          >
            <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#fafafa' }}>
              {closing ? 'İşleniyor…' : 'Evet'}
            </span>
          </button>
          <button
            onClick={() => !closing && setCloseConfirmOpen(false)}
            disabled={closing}
            className="flex-1 rounded-[14px] py-3 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
            style={{ border: '2px solid #3973bf' }}
          >
            <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#3973bf' }}>Hayır</span>
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const editEl = editOpen && selected ? (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={() => !editSaving && setEditOpen(false)} />
      <div className="relative bg-[#f6f6f6] rounded-[24px] w-full max-w-[460px]" style={{ padding: '28px 28px 24px', boxShadow: '0px 8px 32px rgba(0,0,0,0.18)' }}>
        <button
          onClick={() => !editSaving && setEditOpen(false)}
          aria-label="Kapat"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/90 hover:bg-gray-100 flex items-center justify-center"
          style={{ boxShadow: '0px 1px 2px rgba(0,0,0,0.08)' }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" /></svg>
        </button>
        <h2 style={{ fontFamily: 'Inter', fontSize: 22, fontWeight: 700, color: '#e5599b', marginBottom: 18 }}>Ödevi Düzenle</h2>

        <label style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Ödev Başlığı *</label>
        <input
          value={editTitle}
          onChange={e => setEditTitle(e.target.value)}
          className="w-full bg-white rounded-[14px] px-4 py-3 outline-none text-[15px] mb-4"
          style={{ border: `2px solid ${editTitle.trim() ? '#3973bf' : '#e5599b'}`, fontFamily: 'Inter' }}
        />

        <label style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Son Tarih</label>
        <div className="flex gap-2">
          <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)} disabled={editIndefinite}
            className="flex-1 bg-white rounded-[14px] px-4 py-3 outline-none text-[15px]"
            style={{ border: '2px solid #3973bf', fontFamily: 'Inter', opacity: editIndefinite ? 0.4 : 1 }} />
          <input type="time" value={editTime} onChange={e => setEditTime(e.target.value)} disabled={editIndefinite} aria-label="Son saat"
            className="w-[120px] bg-white rounded-[14px] px-4 py-3 outline-none text-[15px]"
            style={{ border: '2px solid #3973bf', fontFamily: 'Inter', opacity: editIndefinite ? 0.4 : 1 }} />
        </div>
        <button onClick={() => setEditIndefinite(v => !v)} className="flex items-center gap-2 mt-2">
          <span className="w-5 h-5 rounded-md flex items-center justify-center" style={{ border: `2px solid ${editIndefinite ? '#e5599b' : '#c5c5c5'}`, background: editIndefinite ? '#e5599b' : '#fff' }}>
            {editIndefinite && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
          </span>
          <span style={{ fontFamily: 'Inter', fontSize: 13, color: '#2b2b2b' }}>Süresiz</span>
        </button>

        {editError && <p className="text-[13px] text-red-500 mt-3">{editError}</p>}

        <div className="flex gap-3 mt-5">
          <button onClick={() => !editSaving && setEditOpen(false)} disabled={editSaving}
            className="flex-1 rounded-[14px] py-3 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors" style={{ border: '2px solid #3973bf' }}>
            <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#3973bf' }}>Vazgeç</span>
          </button>
          <button onClick={saveEdit} disabled={editSaving || !editTitle.trim()}
            className="flex-1 rounded-[14px] py-3 hover:opacity-90 disabled:opacity-50 transition-opacity" style={{ background: '#e5599b' }}>
            <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#fafafa' }}>{editSaving ? 'Kaydediliyor…' : 'Kaydet'}</span>
          </button>
        </div>
      </div>
    </div>
  ) : null;

  // ---- PERFORMANCE VIEW ----
  if (view === 'performance') {
    const completedCount = submissions.filter(s => ['submitted', 'graded', 'returned'].includes(s.status)).length;
    // B32: filter the student list by the selected class + name search. 'all'
    // shows every targeted student ("Bu ödevin atandığı bütün öğrenciler").
    const classOptions = selected.classDetails.filter(c => c.id);
    const classCount = (cid: string) => {
      const rows = submissions.filter(s => s.classId === cid);
      const done = rows.filter(s => ['submitted', 'graded', 'returned'].includes(s.status)).length;
      return { total: rows.length, done };
    };
    const q = studentQuery.trim().toLocaleLowerCase('tr');
    const filteredSubmissions = submissions.filter(s =>
      (selectedClass === 'all' || s.classId === selectedClass) &&
      (q === '' || s.name.toLocaleLowerCase('tr').includes(q)),
    );
    return (
      <div>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <button onClick={() => setView('detail')} className="hover:bg-gray-100 rounded-full p-1 transition-colors">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><path d="M20 8l-8 8 8 8" /></svg>
            </button>
            <h1 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Ödev Detayları</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={closeSelected} className="rounded-[12px] px-5 py-3 hover:opacity-90 transition-opacity" style={{ background: selected?.visibleToStudents ? '#3973bf' : '#16a34a' }}>
              <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#fafafa' }}>{selected?.visibleToStudents ? 'Öğrenciye Kapat' : 'Öğrenciye Aç'}</span>
            </button>
            <button onClick={openEdit} className="rounded-[12px] px-5 py-3 hover:opacity-90 transition-opacity" style={{ background: '#e5599b' }}>
              <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Ödevi Düzenle</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col xl:flex-row gap-5">
          {/* Left: "all students" pink button + per-class cards (B32) */}
          <div className="w-full xl:w-[432px] xl:flex-shrink-0 space-y-4">
            {/* Bu ödevin atandığı bütün öğrenciler */}
            <button
              onClick={() => setSelectedClass('all')}
              className={`w-full rounded-[12px] flex overflow-hidden text-left transition-shadow shadow-[0px_1px_4px_rgba(0,0,0,0.12)] ${selectedClass === 'all' ? 'ring-2 ring-offset-2 ring-[#e5599b]' : 'hover:shadow-md'}`}
            >
              <div className="flex-1 p-4 flex items-center" style={{ background: '#e5599b' }}>
                <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Bu ödevin atandığı bütün öğrenciler</span>
              </div>
              <div className="w-[128px] flex items-center justify-center flex-shrink-0" style={{ background: '#c94183' }}>
                <span style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#fafafa' }}>{completedCount}/{submissions.length}</span>
              </div>
            </button>

            {classOptions.map((cls) => {
              const c = classCount(cls.id!);
              const active = selectedClass === cls.id;
              return (
                <div
                  key={cls.id}
                  onClick={() => setSelectedClass(cls.id!)}
                  className={`bg-[#fafafa] rounded-[12px] flex overflow-hidden cursor-pointer transition-shadow shadow-[0px_1px_4px_rgba(0,0,0,0.12)] ${active ? 'ring-2 ring-offset-2 ring-[#3973bf]' : 'hover:shadow-md'}`}
                >
                  <div className="flex-1 p-4">
                    <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{selected.institution}</p>
                    <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{cls.name}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <svg width="18" height="15" viewBox="0 0 24 20" fill="#2b2b2b"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z" /></svg>
                      <span style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 500, color: '#2b2b2b' }}>{cls.students} Öğrenci</span>
                    </div>
                  </div>
                  <div className="w-[128px] bg-[#3973bf] flex items-center justify-center flex-shrink-0">
                    <span style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#fafafa' }}>{c.done}/{c.total}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right: student table */}
          <div className="flex-1 bg-[#fafafa] rounded-[12px] p-5 shadow-[0px_1px_4px_rgba(0,0,0,0.12)]">
            <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 500, color: '#2b2b2b', marginBottom: 16 }}>
              Öğrenciler - {filteredSubmissions.length}
            </p>

            {/* Öğrenci ara (B32) */}
            <div className="relative mb-4">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
              <input
                value={studentQuery}
                onChange={(e) => setStudentQuery(e.target.value)}
                placeholder="Öğrenci ara"
                style={{ fontFamily: 'Inter', fontSize: 14 }}
                className="w-full pl-10 pr-3 py-2.5 rounded-[10px] bg-white border border-[#e5e7eb] outline-none focus:border-[#3973bf]"
              />
            </div>

            {/* Table header */}
            <div className="flex items-center mb-3 px-3">
              <div className="flex-1" />
              {['Puan', 'Durum', 'Süre'].map((h) => (
                <div key={h} className="w-[100px] text-center">
                  <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{h}</span>
                </div>
              ))}
            </div>

            {/* Student rows — fixed height, scrollable */}
            {loadingSubmissions ? (
              <div className="flex justify-center py-8"><div className="w-6 h-6 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
            ) : submissions.length === 0 ? (
              <p className="text-center text-gray-400 py-8">Henüz gönderim yok.</p>
            ) : filteredSubmissions.length === 0 ? (
              <p className="text-center text-gray-400 py-8">{q ? 'Aramanızla eşleşen öğrenci yok.' : 'Bu sınıfta öğrenci yok.'}</p>
            ) : (
              <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                {filteredSubmissions.map((s) => {
                  const statusLabel = s.status === 'graded' ? 'Notlandı' : s.status === 'submitted' ? 'Gönderildi' : s.status === 'returned' ? 'İade Edildi' : 'Bekleniyor';
                  const statusColor = s.status === 'graded' ? '#12D700' : s.status === 'submitted' ? '#f1972f' : s.status === 'returned' ? '#6d79ff' : '#c5c5c5';
                  return (
                    <div
                      key={s.id}
                      onClick={() => openGrading(s)}
                      className="flex items-center rounded-[12px] overflow-hidden cursor-pointer shadow-[0px_1px_4px_rgba(0,0,0,0.12)] hover:shadow-md transition-shadow"
                      title="Gönderiyi görüntüle ve notlandır"
                    >
                      <div className="flex items-center gap-3 flex-1 bg-[#fafafa] p-3 rounded-l-[12px]">
                        <div className="w-[40px] h-[40px] rounded-full bg-[#3973bf] flex-shrink-0 overflow-hidden">
                          <img src="/a-avatar.png" alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>{s.name}</span>
                          <span style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#46923c' }}>{s.status === 'submitted' ? " (Notlandırma bekliyor)" : ""}</span>
                          {s.late && (
                            <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5" style={{ fontFamily: 'Inter', fontSize: 11, fontWeight: 700, color: '#dc2626', background: '#fee2e2' }}>Geç Teslim</span>
                          )}
                          {s.submissionFileUrl && (
                            <span className="block text-[12px] text-[#e5599b] font-semibold">📎 Dosya yüklendi</span>
                          )}
                        </div>
                      </div>
                      <div className="flex bg-[#3973bf] rounded-r-[12px]">
                        <div className="w-[100px] py-4 text-center">
                          <span style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#fafafa' }}>{s.score != null ? s.score : '—'}</span>
                        </div>
                        <div className="w-[100px] py-4 text-center">
                          <span style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: statusColor }}>{statusLabel}</span>
                        </div>
                        <div className="w-[100px] py-4 text-center">
                          <span style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#fafafa' }}>{s.time}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Per-student submission + grading popup */}
        {gradingFor && (() => {
          const url = gradingFor.submissionFileUrl;
          const ext = url ? (url.split('?')[0].split('.').pop() || '').toLowerCase() : '';
          const isPdf = ext === 'pdf';
          const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => { if (!savingGrade) setGradingFor(null); }} />
              <div className="relative bg-[#f7f7f7] rounded-[20px] w-full max-w-[900px] max-h-[88vh] flex flex-col p-7" style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.15)' }}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 style={{ fontFamily: 'Inter', fontSize: 22, fontWeight: 700, color: '#2b2b2b' }}>{gradingFor.name}</h2>
                    <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#888' }}>{selected.title}</p>
                  </div>
                  <button onClick={() => { if (!savingGrade) setGradingFor(null); }} className="w-[36px] h-[36px] rounded-full flex items-center justify-center hover:bg-gray-200">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" /></svg>
                  </button>
                </div>

                <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0 overflow-y-auto lg:overflow-visible">
                  {/* Left: the student's submission */}
                  <div className="flex-1 min-w-0 overflow-y-auto">
                    <p style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#3973bf', marginBottom: 8 }}>Öğrenci Teslimi</p>
                    {url ? (
                      <div className="space-y-3">
                        {isPdf && <iframe src={url} title="Öğrenci Dosyası" className="w-full h-[420px] rounded-[12px] border border-gray-200 bg-white" />}
                        {isImage && <img src={url} alt="Öğrenci Dosyası" className="w-full rounded-[12px] border border-gray-200 bg-white" />}
                        <a href={url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 border-2 border-[#3973bf] bg-[#f0f4fa] rounded-[12px] px-4 py-2 hover:bg-[#e0eaf5] transition-colors">
                          <span className="text-[13px] font-semibold text-[#3973bf]">Yeni Sekmede Görüntüle</span>
                        </a>
                      </div>
                    ) : (
                      <div className="bg-white border border-dashed border-gray-300 rounded-[12px] p-6 text-center text-[13px] text-gray-400">
                        Öğrenci henüz dosya yüklemedi.
                      </div>
                    )}
                    {gradingFor.studentNotes && (
                      <div className="mt-4 bg-white rounded-[12px] p-4 border border-gray-200">
                        <p className="text-[12px] text-gray-500 mb-1">Öğrenci Notu</p>
                        <p className="text-[13px] text-[#2b2b2b] whitespace-pre-wrap">{gradingFor.studentNotes}</p>
                      </div>
                    )}
                  </div>

                  {/* Right: grade + comment */}
                  <div className="w-full lg:w-[320px] lg:flex-shrink-0 flex flex-col">
                    <label style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#2b2b2b' }} className="block mb-2">Puan (0-100)</label>
                    <input
                      type="number" min={0} max={100} value={gradeScore}
                      onChange={(e) => setGradeScore(e.target.value)}
                      placeholder="Örn: 85"
                      className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[15px] mb-4"
                      style={{ border: '2px solid #3973bf', fontFamily: 'Inter' }}
                    />
                    <label style={{ fontFamily: 'Inter', fontSize: 14, fontWeight: 600, color: '#2b2b2b' }} className="block mb-2">Yorum (opsiyonel)</label>
                    <textarea
                      value={gradeComment} onChange={(e) => setGradeComment(e.target.value)} rows={6}
                      placeholder="Öğrenciye geri bildirim yaz…"
                      className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[15px] resize-none flex-1"
                      style={{ border: '2px solid #3973bf', fontFamily: 'Inter' }}
                    />
                    {gradeError && <p className="text-[13px] text-red-500 mt-2">{gradeError}</p>}
                    <button
                      onClick={saveGrade}
                      disabled={savingGrade}
                      className="mt-4 w-full rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50"
                      style={{ background: '#e5599b' }}
                    >
                      <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#fafafa' }}>{savingGrade ? 'Kaydediliyor…' : 'Kaydet ve Notlandır'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        {modalEl}
        {closeConfirmEl}
        {editEl}
      </div>
    );
  }

  // ---- DETAIL VIEW ----
  if (view === 'detail') {
    // Deep-links to /teacher/odevlerim/:id (e.g. the library's "Ödeve Geri Dön")
    // mount straight into the detail view before the assignment list has loaded,
    // so `selected` is briefly undefined. Guard instead of crashing on
    // selected.color; show a spinner while loading, else a not-found fallback.
    if (!selected) {
      return (
        <div>
          {loading ? (
            <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="py-20 text-center">
              <p style={{ fontFamily: 'Inter', fontSize: 16, color: '#6b7280', marginBottom: 16 }}>Ödev bulunamadı.</p>
              <button onClick={() => setView('list')} className="rounded-[12px] px-5 py-3" style={{ background: '#3973bf' }}>
                <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Ödevlere Dön</span>
              </button>
            </div>
          )}
        </div>
      );
    }
    return (
      <div>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <button onClick={() => { setPdfOpen(false); setView('list'); }} className="hover:bg-gray-100 rounded-full p-1 transition-colors">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round"><path d="M20 8l-8 8 8 8" /></svg>
            </button>
            <h1 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Ödev Detayları</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={closeSelected} className="rounded-[12px] px-5 py-3 hover:opacity-90 transition-opacity" style={{ background: selected?.visibleToStudents ? '#3973bf' : '#16a34a' }}>
              <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#fafafa' }}>{selected?.visibleToStudents ? 'Öğrenciye Kapat' : 'Öğrenciye Aç'}</span>
            </button>
            <button onClick={openEdit} className="rounded-[12px] px-5 py-3 hover:opacity-90 transition-opacity" style={{ background: '#e5599b' }}>
              <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Ödevi Düzenle</span>
            </button>
          </div>
        </div>

        <div className="bg-[#fafafa] rounded-[16px] p-6" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
          {/* Header */}
          <div className="flex gap-5 mb-5">
            <div className="w-[97px] h-[97px] rounded-[12px] flex items-center justify-center flex-shrink-0" style={{ background: selected.color }}>
              <img src="/a-odevicon.png" alt="" className="w-[49px] h-[46px] object-contain" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 700, color: '#2b2b2b' }}>{selected.title}</p>
                <div className="w-[30px] h-[30px] rounded-full bg-[#ffda09] flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1e1e1e" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                </div>
                {!selected.visibleToStudents && (
                  <span style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 9999, padding: '4px 12px' }}>
                    Öğrenciye kapalı
                  </span>
                )}
              </div>
              <p style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 500, color: '#2b2b2b' }}>Açılış: {selected.openDate}</p>
              <p style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 500, color: '#2b2b2b' }}>Kapanış: {selected.closeDate}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p style={{ fontFamily: 'Inter', fontSize: 18, fontWeight: 600, color: '#2b2b2b' }}>Atanılan Sınıflar</p>
              <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>{selected.institution}</p>
              <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#2b2b2b' }}>{selected.classes}</p>
            </div>
          </div>

          {/* Divider */}
          <div className="h-[1px] bg-[#c5c5c5] mb-5" />

          <div className="flex flex-col xl:flex-row gap-8">
            {/* Content detail */}
            <div className="flex-1">
              <p style={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 600, color: '#2b2b2b', marginBottom: 16 }}>Ödev İçeriği Detayı</p>
              {/* B28: only render fields that have a real value (skip empty "—") */}
              <div className="space-y-2 mb-4">
                <p><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#3973bf' }}>İçerik Tipi: </span><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{selected.contentType}</span></p>
                {selected.book && selected.book !== '—' && (
                  <p><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#3973bf' }}>Kitap Seçimi: </span><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{selected.book}</span></p>
                )}
                {selected.unit && selected.unit !== '—' && (
                  <p><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#3973bf' }}>Ünite: </span><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{selected.unit}</span></p>
                )}
                {selected.note && (
                  <p style={{ whiteSpace: 'pre-wrap' }}><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#3973bf' }}>Açıklama: </span><span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{selected.note}</span></p>
                )}
              </div>
              {selected.tests.length > 0 && (
                <>
                  <p style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#3973bf', marginBottom: 8 }}>Ödev İçeriği:</p>
                  <div className="space-y-2">
                    {selected.tests.map((t, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-[10px] h-[10px] rounded-full bg-[#e5599b] flex-shrink-0" />
                        <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{t}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <AssignmentOutcomes
                hw={selected}
                onSaved={(los) => setAssignments(prev => prev.map(x => x.id === selected.id ? { ...x, learningOutcomes: los } : x))}
              />
            </div>

            {/* Action buttons */}
            <div className="w-full xl:w-[431px] xl:flex-shrink-0 space-y-3">
              {selected.attachmentUrl && (
                <button
                  onClick={() => setPdfOpen(true)}
                  className="w-full rounded-[12px] py-3 text-center transition-colors hover:bg-pink-50 flex items-center justify-center gap-2"
                  style={{ border: '2px solid #e5599b' }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e5599b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#e5599b' }}>Ödev İçeriğini Görüntüle</span>
                </button>
              )}
              <button
                onClick={openPerformance}
                className="w-full rounded-[12px] py-3 text-center transition-colors hover:bg-blue-50"
                style={{ border: '2px solid #3973bf' }}
              >
                <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#3973bf' }}>Performansları Görüntüle</span>
              </button>
              {selected.answerKey ? (
                selected.answersReleased ? (
                  <div className="w-full rounded-[12px] py-3 text-center bg-[#59e56e]/10 flex items-center justify-center gap-2" style={{ border: '2px solid #59e56e' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#179e3b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#179e3b' }}>Cevaplar Yayınlandı</span>
                  </div>
                ) : (
                  <button
                    onClick={releaseAnswers}
                    disabled={releasing}
                    className="w-full rounded-[12px] py-3 text-center transition-colors hover:bg-green-50 disabled:opacity-50"
                    style={{ border: '2px solid #59e56e' }}
                  >
                    <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#179e3b' }}>
                      {releasing ? 'Yayınlanıyor…' : 'Cevapları Yayınla'}
                    </span>
                  </button>
                )
              ) : null}
              <button
                onClick={() => {
                  if (selected?.bookId) {
                    // B27: forward the assignment's own section so the library
                    // opens straight to THAT test's çözüm videos, not the book root.
                    // openAssignmentId lets the video viewer's back button return
                    // to this homework detail ("Ödeve Geri Dön") instead of the book.
                    navigate('/teacher/kutuphanem', { state: { bookId: selected.bookId, sectionLabel: selected.sectionLabel ?? undefined, openAssignmentId: selected.id } });
                  }
                }}
                disabled={!selected?.bookId}
                className="w-full rounded-[12px] py-3 text-center transition-colors hover:bg-pink-50 disabled:opacity-40"
                style={{ border: '2px solid #e5599b' }}
              >
                <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#e5599b' }}>
                  {selected?.bookId ? 'Soruları Görüntüle' : 'Kitap bağlantısı yok'}
                </span>
              </button>
            </div>
          </div>
        </div>
        {modalEl}
        {pdfOpen && selected.attachmentUrl && (() => {
          const url = selected.attachmentUrl!;
          const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
          const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
          return (
            <div className="fixed inset-0 z-[60] bg-black/60 flex flex-col p-4 sm:p-8" onClick={() => setPdfOpen(false)}>
              <div className="flex items-center justify-between mb-3" onClick={(e) => e.stopPropagation()}>
                <p style={{ fontFamily: 'Inter' }} className="text-white text-[16px] font-semibold">{selected.title} — Ödev İçeriği</p>
                <div className="flex gap-2">
                  <a href={url} target="_blank" rel="noopener noreferrer"
                    className="rounded-[10px] px-4 py-2 bg-white/90 hover:bg-white text-[14px] font-semibold text-[#2b2b2b]">Yeni Sekmede Aç</a>
                  <button onClick={() => setPdfOpen(false)}
                    className="rounded-[10px] px-4 py-2 bg-[#e5599b] hover:bg-pink-600 text-[14px] font-semibold text-white">Kapat</button>
                </div>
              </div>
              <div className="flex-1 min-h-0 bg-white rounded-[12px] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                {isImage ? (
                  <div className="w-full h-full overflow-auto flex items-start justify-center bg-[#333]">
                    <img src={url} alt="Ödev İçeriği" className="max-w-full" />
                  </div>
                ) : (
                  <iframe src={url} title="Ödev İçeriği" className="w-full h-full border-0" />
                )}
              </div>
            </div>
          );
        })()}
        {closeConfirmEl}
        {editEl}
      </div>
    );
  }

  // ---- LIST VIEW with Aktif / Süresi Dolmuş tabs ----
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h1 style={{ fontFamily: 'Inter', fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Ödevler</h1>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/teacher/odevlerim/performans')} className="rounded-[12px] px-5 py-3 flex items-center gap-2 hover:bg-gray-50 transition-colors" style={{ background: '#fff', border: '1px solid #e5599b' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e5599b" strokeWidth="2"><path d="M21 21H4a1 1 0 0 1-1-1V3" /><path d="M7 14l4-4 3 3 5-6" /></svg>
            <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#e5599b' }}>Öğrenci Performansı Görüntüle</span>
          </button>
          <button onClick={() => { setEditData(null); setCreateOpen(true); }} className="rounded-[12px] px-5 py-3 flex items-center gap-2 hover:opacity-90 transition-opacity" style={{ background: '#e5599b' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fafafa" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            <span style={{ fontFamily: 'Inter', fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Ödev Oluştur</span>
          </button>
        </div>
      </div>

      {/* Tabs + target filter */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        {([
          { key: 'active' as Tab, label: 'Aktif Ödevler', count: activeHw.length, bg: '#e5599b' },
          { key: 'expired' as Tab, label: 'Süresi Dolmuş', count: expiredHw.length, bg: '#3973bf' },
        ]).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="rounded-[12px] px-6 py-3 flex items-center gap-2 transition-opacity"
            style={{ background: tab === t.key ? t.bg : '#e5e5e5', opacity: tab === t.key ? 1 : 0.85 }}>
            <span style={{ fontFamily: 'Inter', fontSize: 15, fontWeight: 700, color: tab === t.key ? '#fafafa' : '#2b2b2b' }}>
              {t.label} ({t.count})
            </span>
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          {([
            { key: 'all' as TargetFilter, label: 'Tümü' },
            { key: 'class' as TargetFilter, label: 'Sınıf' },
            { key: 'individual' as TargetFilter, label: 'Bireysel' },
          ]).map(f => (
            <button key={f.key} onClick={() => setTargetFilter(f.key)}
              className="rounded-[10px] px-4 py-2 text-[13px] font-semibold transition-colors"
              style={{ background: targetFilter === f.key ? '#3973bf' : '#e5e5e5', color: targetFilter === f.key ? '#fff' : '#2b2b2b', fontFamily: 'Inter' }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-[#e5599b] border-t-transparent rounded-full animate-spin" /></div>
      ) : visibleHw.length === 0 ? (
        <p className="text-center text-gray-400 py-10" style={{ fontFamily: 'Inter' }}>
          {tab === 'active' ? 'Aktif ödeviniz yok.' : 'Süresi dolmuş ödeviniz yok.'}
        </p>
      ) : (
        <div className="flex gap-5 flex-wrap">
          {visibleHw.map(hw => (
            <AssignmentCard key={hw.id} hw={hw} onClick={() => openDetail(hw.id)} />
          ))}
        </div>
      )}

      {modalEl}

      {closeConfirmEl}
      {editEl}
    </div>
  );
};

export default TeacherAssignments;
